import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Search, X } from 'lucide-react';
import { formatHubBranchLabel } from '../../utils/destinationType.js';
import { selectDropdownOpenClass } from '../common/uiClasses.js';

/**
 * Form multi-select for destination location hubs mapped to a source hub.
 */
const DestinationHubMultiSelect = ({
  hubs = [],
  value = [],
  onChange,
  placeholder = 'Select destination hubs',
  disabled = false,
  className = '',
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [menuStyle, setMenuStyle] = useState(null);
  const containerRef = useRef(null);
  const panelRef = useRef(null);
  const selected = Array.isArray(value) ? value.map(String) : [];

  const hubsById = useMemo(() => {
    const map = {};
    (hubs || []).forEach((hub) => {
      if (hub?.id != null) map[String(hub.id)] = hub;
    });
    return map;
  }, [hubs]);

  const options = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (hubs || []).filter((hub) => {
      if (!q) return true;
      const label = `${formatHubBranchLabel(hub)} ${hub.location || ''} ${hub.location_code || ''} ${hub.hub_id || ''}`.toLowerCase();
      return label.includes(q);
    });
  }, [hubs, search]);

  useEffect(() => {
    const onClick = (event) => {
      const target = event.target;
      if (containerRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  useLayoutEffect(() => {
    if (!open) {
      setMenuStyle(null);
      return undefined;
    }

    const updatePosition = () => {
      const node = containerRef.current;
      if (!node) return;
      const rect = node.getBoundingClientRect();
      const gap = 4;
      const margin = 8;
      const headerBottom = document.querySelector('header')?.getBoundingClientRect().bottom || 0;
      const topLimit = headerBottom + margin;
      const bottomLimit = window.innerHeight - margin;
      const spaceBelow = bottomLimit - rect.bottom;
      const spaceAbove = rect.top - topLimit;
      const openUp = spaceBelow < 220 && spaceAbove > spaceBelow;
      const available = Math.max(140, (openUp ? spaceAbove : spaceBelow) - gap);
      const left = Math.max(margin, Math.min(rect.left, window.innerWidth - rect.width - margin));
      setMenuStyle({
        position: 'fixed',
        left,
        width: Math.min(rect.width, window.innerWidth - margin * 2),
        maxHeight: available,
        listMaxHeight: Math.max(72, available - 108),
        zIndex: 9999,
        ...(openUp
          ? { top: 'auto', bottom: window.innerHeight - rect.top + gap }
          : { top: rect.bottom + gap, bottom: 'auto' }),
      });
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open]);

  const toggle = (hubId) => {
    const id = String(hubId);
    const next = selected.includes(id)
      ? selected.filter((item) => item !== id)
      : [...selected, id];
    onChange(next);
  };

  const remove = (hubId) => {
    onChange(selected.filter((item) => item !== String(hubId)));
  };

  const optionIds = useMemo(
    () => options.map((hub) => String(hub.id)).filter(Boolean),
    [options],
  );

  const allFilteredSelected =
    optionIds.length > 0 && optionIds.every((id) => selected.includes(id));

  const handleSelectAllToggle = () => {
    if (allFilteredSelected) {
      // Deselect only the currently visible/filtered options
      const removeSet = new Set(optionIds);
      onChange(selected.filter((id) => !removeSet.has(id)));
      return;
    }
    // Select all visible options, keep any already-selected outside the filter
    const next = new Set(selected);
    optionIds.forEach((id) => next.add(id));
    onChange(Array.from(next));
  };

  const triggerLabel = selected.length
    ? `${selected.length} destination${selected.length === 1 ? '' : 's'} selected`
    : placeholder;

  return (
    <div ref={containerRef} className={`relative w-full ${className}`}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen((v) => !v)}
        className={`w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base text-left flex justify-between items-center gap-2 ${
          disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white cursor-pointer'
        } ${open ? selectDropdownOpenClass : ''}`}
      >
        <span className={`min-w-0 truncate ${selected.length ? 'text-gray-800' : 'text-gray-400'}`}>
          {triggerLabel}
        </span>
        <ChevronDown className={`h-4 w-4 shrink-0 text-gray-500 ${open ? 'rotate-180' : ''}`} />
      </button>

      {selected.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {selected.map((id) => {
            const hub = hubsById[id];
            const label = hub
              ? `${formatHubBranchLabel(hub) || hub.location}${hub.location_code ? ` (${hub.location_code})` : ''}`
              : id;
            return (
              <span
                key={id}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-xs font-medium bg-blue-50 text-blue-800 border border-blue-200"
              >
                {label}
                {!disabled && (
                  <button
                    type="button"
                    onClick={() => remove(id)}
                    className="p-0.5 rounded hover:bg-blue-100"
                    aria-label={`Remove ${label}`}
                  >
                    <X className="w-3 h-3" />
                  </button>
                )}
              </span>
            );
          })}
        </div>
      )}

      {open && !disabled && menuStyle && createPortal(
        <div
          ref={panelRef}
          className="bg-white border border-gray-300 rounded-xl shadow-lg flex flex-col overflow-hidden"
          style={{
            position: menuStyle.position,
            left: menuStyle.left,
            width: menuStyle.width,
            top: menuStyle.top,
            bottom: menuStyle.bottom,
            maxHeight: menuStyle.maxHeight,
            zIndex: menuStyle.zIndex,
          }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div className="p-2 border-b border-gray-200 space-y-2 shrink-0">
            <div className="relative">
              <Search
                className="pointer-events-none absolute left-3 top-1/2 z-[1] h-4 w-4 -translate-y-1/2 text-gray-400"
                aria-hidden="true"
              />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search destination hub..."
                autoFocus
                style={{ paddingLeft: '2.5rem' }}
                className="w-full py-1.5 pr-3 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
            </div>
            <div className="flex items-center justify-between gap-2">
              <button
                type="button"
                onClick={handleSelectAllToggle}
                disabled={optionIds.length === 0}
                className="text-xs font-semibold text-blue-600 hover:text-blue-800 disabled:text-gray-400 disabled:cursor-not-allowed"
              >
                {allFilteredSelected ? 'Deselect All' : 'Select All'}
              </button>
              {selected.length > 0 && (
                <button
                  type="button"
                  onClick={() => onChange([])}
                  className="text-xs font-semibold text-gray-500 hover:text-gray-700"
                >
                  Clear all
                </button>
              )}
            </div>
          </div>
          <div
            className="min-h-0 overflow-y-auto overscroll-contain"
            style={{ maxHeight: menuStyle.listMaxHeight }}
          >
            {options.map((hub) => {
              const id = String(hub.id);
              return (
                <label
                  key={id}
                  className="flex items-center gap-2 px-3 py-2 hover:bg-blue-50 cursor-pointer text-sm text-gray-800"
                >
                  <input
                    type="checkbox"
                    checked={selected.includes(id)}
                    onChange={() => toggle(id)}
                    className="accent-blue-500"
                  />
                  <span className="truncate">
                    {formatHubBranchLabel(hub) || hub.location}
                    {hub.location_code ? ` (${hub.location_code})` : ''}
                  </span>
                </label>
              );
            })}
            {options.length === 0 && (
              <div className="px-3 py-2 text-sm text-gray-400">No destination hubs found</div>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default DestinationHubMultiSelect;
